import React, { memo, useCallback, useMemo } from "react";
import CopyListPopover from "./copy-list-popover";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useUsers } from "modules/providers/users-provider";
import AddItemButton from "../components/add-item-button";
import { HStack, Text } from "@chakra-ui/react";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { isUserInteractive } from "modules/utils/Functions";
import { MessageThreadType, UserRoleType } from "gql/graphql";
import UserAvatar from "modules/_shared/user-avatar";
import UserCallout from "modules/_shared/user-callout";
import colors from "app/colors";
import DistributionGroupOptionLabel from "modules/selectors/distribution-group-option-label";
import { IQuillEditorHandle } from "modules/messaging/compose/quill-message-composer";
import { MENTION_TYPE } from "modules/utils/Constants";

interface IProps {
  draftId: string;
  quillEditorRef: React.RefObject<IQuillEditorHandle>;
}

const CopyCalloutsPopover = ({ draftId, quillEditorRef }: IProps) => {
  const { draft, mentionIds } = useMessageThreadDraft({ draftId });
  const { distributionGroupsById } = useDistributionGroups();
  const { usersById } = useUsers();

  const relatedMessageThread = draft?.relatedMessageThread;

  const _userIds = useMemo(
    () =>
      relatedMessageThread
        ? relatedMessageThread.participantUserIdList.filter((userId) => {
            const user = usersById[userId];
            if (!user) return false;
            if (mentionIds.includes(userId)) return false;
            if (!user.active) return false;
            if (!isUserInteractive(user)) return false;
            return true;
          })
        : [],
    [mentionIds, relatedMessageThread, usersById],
  );

  const _distributionGroupIds = useMemo(
    () =>
      relatedMessageThread
        ? relatedMessageThread.distributionGroupIdList
            .filter(
              (distributionGroupId) =>
                !mentionIds.includes(distributionGroupId),
            )
            .filter((id) => distributionGroupsById[id]?.active === true)
        : [],
    [distributionGroupsById, mentionIds, relatedMessageThread],
  );

  const onSaveClicked = useCallback(
    (selectedIds: string[]) => {
      selectedIds.forEach((id) => {
        const user = usersById[id];
        const distributionGroup = distributionGroupsById[id];
        if (user) {
          quillEditorRef.current?.appendMention({
            id,
            isExternal: user.roleType === UserRoleType.Email,
            type: MENTION_TYPE.USER,
            value: user.callout ?? "",
          });
        }
        if (distributionGroup) {
          quillEditorRef.current?.appendMention({
            id,
            isExternal: distributionGroup.emailUserIdList.length > 0,
            type: MENTION_TYPE.DISTRIBUTION_GROUP,
            value: distributionGroup.name,
          });
        }
      });
    },
    [distributionGroupsById, quillEditorRef, usersById],
  );

  const isDisabled =
    _userIds.length === 0 && _distributionGroupIds.length === 0;

  const sections = useMemo(() => {
    if (_distributionGroupIds.length > 0 && _userIds.length > 0) {
      return [
        {
          title: "GROUPS",
          ids: _distributionGroupIds,
        },
        {
          title: "USERS",
          ids: _userIds,
        },
      ];
    }
    if (_distributionGroupIds.length > 0) {
      return [
        {
          title: "GROUPS",
          ids: _distributionGroupIds,
        },
      ];
    }
    return [
      {
        title: "USERS",
        ids: _userIds,
      },
    ];
  }, [_distributionGroupIds, _userIds]);

  const ids = useMemo(
    () => [..._distributionGroupIds, ..._userIds],
    [_distributionGroupIds, _userIds],
  );

  const renderRow = useCallback(
    (_: number, _rowIndex: number) => {
      const id = ids[_rowIndex];
      const user = usersById[id];
      const distributionGroup = distributionGroupsById[id];

      return (
        <>
          {distributionGroup && (
            <HStack w="full" h={10}>
              <DistributionGroupOptionLabel
                distributionGroup={distributionGroup}
                search=""
              />
            </HStack>
          )}
          {user && (
            <HStack w="full" h={8}>
              <UserAvatar userId={user.id} size="xs" />
              <Text fontSize={12} fontWeight={700} color={colors.blue}>
                <UserCallout userId={user.id} />
              </Text>
            </HStack>
          )}
        </>
      );
    },
    [distributionGroupsById, ids, usersById],
  );

  const title = useMemo(() => {
    if (_userIds.length + _distributionGroupIds.length > 0) {
      return `Add People (${_userIds.length + _distributionGroupIds.length})`;
    }
    return "Add People";
  }, [_distributionGroupIds.length, _userIds.length]);

  if (!relatedMessageThread) {
    return null;
  }

  /* do not display the callouts popover if forwarding to a chat */
  /*The issue is around keeping the callouts displayed in the 'send to' field and the ones in the actual message in sync. When a user adds a callout to the message it's adding it to the sendTo field but not the other way around. */
  if (MessageThreadType.Chat === draft?.type) {
    return null;
  }

  return (
    <CopyListPopover
      sections={sections}
      title="People"
      saveTitle="Add People"
      renderRow={renderRow}
      onSaveClicked={onSaveClicked}
    >
      <AddItemButton isDisabled={isDisabled}>{title}</AddItemButton>
    </CopyListPopover>
  );
};

export default memo(CopyCalloutsPopover);
